/**
 * Conteos por nave de una línea de inventario (`lineas[].conteos`, ver
 * ../api/docs/api/inventarios.md). Funciones puras.
 */

/**
 * Conteos por nave de una línea, ordenados por almacén. Los almacen/stockTeorico/
 * cantidadContada de la línea son derivados de compatibilidad: no se usan
 * @param {Object} linea - Línea del inventario
 * @returns {Array<{almacen: string, stockTeorico: number|null, cantidadContada: number}>} Conteos de la línea
 */
export const conteosDeLinea = (linea) =>
    [...(linea?.conteos ?? [])].sort((a, b) => String(a.almacen).localeCompare(String(b.almacen)));

/**
 * Suma de lo contado en todas las naves
 * @param {Array<{cantidadContada: number}>} conteos - Conteos de la línea
 * @returns {number|null} Total, o null si no se ha contado en ninguna nave
 */
export const totalContado = (conteos) =>
    conteos.length === 0 ? null : conteos.reduce((total, conteo) => total + conteo.cantidadContada, 0);

/**
 * Diferencia de una línea sumando la de cada nave. Solo cuadra si cuadran TODAS:
 * sobrar 5 en una nave y faltar 5 en otra suma 0, pero es un descuadre de stock.
 * @param {Object} linea - Línea del inventario
 * @returns {{diferencia: number|null, cuadra: boolean, contado: boolean}} Igual que calcularDiferencia
 */
export const diferenciaDeLinea = (linea) => {
    const conteos = conteosDeLinea(linea);
    if (conteos.length === 0) return { diferencia: null, cuadra: false, contado: false };

    const porNave = conteos.map((conteo) => conteo.cantidadContada - (conteo.stockTeorico ?? 0));
    return {
        diferencia: porNave.reduce((total, diferencia) => total + diferencia, 0),
        cuadra: porNave.every((diferencia) => diferencia === 0),
        contado: true,
    };
};

/**
 * Naves en las que se ha contado algo del inventario, sin repetir y ordenadas
 * @param {{lineas: Array}} inventario - Inventario con sus líneas
 * @returns {Array<string>} Códigos de almacén
 */
export const navesContadas = (inventario) =>
    [...new Set((inventario?.lineas ?? []).flatMap((linea) => conteosDeLinea(linea).map((conteo) => conteo.almacen)))].sort();

/**
 * Conteos de una línea tal y como se ven en pantalla: lo guardado con el borrador
 * encima. `pendiente`: tiene cambios sin enviar; `guardado`: esa nave existe en el servidor
 * @param {Array} delServidor - conteosDeLinea de la línea guardada
 * @param {Object<string, number>} enBorrador - { almacen: cantidad } sin guardar
 * @returns {Array} Conteos ordenados por almacén
 */
export const fusionarConteos = (delServidor, enBorrador) => {
    const guardados = delServidor
        .filter((conteo) => !(conteo.almacen in enBorrador))
        .map((conteo) => ({ ...conteo, guardado: true }));
    const pendientes = Object.entries(enBorrador).map(([almacen, cantidadContada]) => {
        const guardado = delServidor.find((conteo) => conteo.almacen === almacen);
        return { almacen, cantidadContada, stockTeorico: guardado?.stockTeorico ?? null, pendiente: true, guardado: Boolean(guardado) };
    });
    return [...guardados, ...pendientes].sort((a, b) => a.almacen.localeCompare(b.almacen));
};

/**
 * Borrador sin la cantidad de una línea en una nave; la línea desaparece si se queda vacía
 * @param {Object<number, Object<string, number>>} borrador - { lineaId: { almacen: cantidad } }
 * @param {number} lineaId - Línea
 * @param {string} almacen - Nave
 * @returns {Object} Borrador nuevo (el mismo objeto si no había nada que quitar)
 */
export const quitarDelBorrador = (borrador, lineaId, almacen) => {
    if (!(almacen in (borrador[lineaId] ?? {}))) return borrador;
    const { [almacen]: _quitada, ...resto } = borrador[lineaId];
    if (Object.keys(resto).length > 0) return { ...borrador, [lineaId]: resto };
    const { [lineaId]: _linea, ...otras } = borrador;
    return otras;
};
